import React, { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../config";
import { CheckCircle, XCircle, ArrowRight, Home, Award } from "lucide-react";

const IeltsResultPage = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("result_id");

    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const loadResult = (id) => {
      axios.get(`${config.apiUrl}/ielts-results/my-results/${id}`, { headers })
        .then((res) => {
          setResult(res.data.result);
          setLoading(false);
        })
        .catch(() => {
          setError("Failed to load result.");
          setLoading(false);
        });
    };

    if (id) {
      loadResult(id);
    } else {
      // No result_id in URL — fetch user's most recent result
      axios.get(`${config.apiUrl}/ielts-results/my-results`, { headers })
        .then((res) => {
          const results = res.data.results || [];
          if (results.length > 0) {
            setResult(results[0]);
          } else {
            setError("No results found.");
          }
          setLoading(false);
        })
        .catch(() => {
          setError("Failed to load result.");
          setLoading(false);
        });
    }
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin" />
        <p className="text-gray-500 font-medium">Loading result…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <p className="text-red-600 font-medium">{error}</p>
    </div>
  );

  if (!result) return null;

  const listeningDetail = result.listening_answers?.listening || {};
  const readingDetail = result.reading_answers?.reading || {};

  const BandCard = ({ label, band, score, max, section, detail }) => (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wide">{label}</h3>
      </div>

      {band !== null ? (
        <>
          <div className="p-5">
            <div className="flex items-end gap-3 mb-4">
              <span className="text-5xl font-black text-gray-900">{band}</span>
              <span className="text-sm font-bold text-gray-500 mb-1">Band</span>
              {score !== null && (
                <span className="ml-auto text-right">
                  <span className="text-2xl font-bold text-indigo-600">{score}</span>
                  <span className="text-xs text-gray-500">/{max}</span>
                </span>
              )}
            </div>

            {/* Band progress bar */}
            <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  band >= 8 ? "bg-green-500" : band >= 6.5 ? "bg-blue-500" : band >= 5.5 ? "bg-amber-500" : "bg-red-500"
                }`}
                style={{ width: `${(band / 9) * 100}%` }}
              />
            </div>

            <p className="text-xs text-gray-500 mt-1">
              {band >= 8 ? "Expert" : band >= 6.5 ? "Good" : band >= 5.5 ? "Average" : "Needs Improvement"}
            </p>
          </div>

          {/* Answer breakdown */}
          {detail && Object.keys(detail).length > 0 && (
            <div className="border-t border-gray-100 px-5 py-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Answer Review</p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {Object.entries(detail).map(([num, info]) => (
                  <div key={num} className="flex items-center gap-3 text-sm">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-gray-100 text-[10px] font-bold text-gray-600">
                      {num}
                    </span>
                    {info.is_correct ? (
                      <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                    )}
                    <span className={`flex-1 truncate text-xs ${info.is_correct ? "text-green-700" : "text-red-600"}`}>
                      {info.student_answer || <span className="italic text-gray-400">Skipped</span>}
                    </span>
                    {!info.is_correct && info.correct_answer && (
                      <span className="text-xs font-semibold text-green-600 shrink-0">
                        → {info.correct_answer}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="p-5 text-center">
          <div className="h-12 w-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-2">
            <span className="text-2xl">⏳</span>
          </div>
          <p className="text-sm font-semibold text-amber-700">Not attempted</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="shrink-0 flex items-center gap-2">
            {(localStorage.getItem("sidebarLogoImg") || localStorage.getItem("ieltsLogoImg")) ? (
              <img
                src={localStorage.getItem("sidebarLogoImg") || localStorage.getItem("ieltsLogoImg")}
                alt=""
                className="h-8 w-8 object-contain rounded"
              />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center font-black text-white text-sm">
                {localStorage.getItem("sidebarLogoText") || localStorage.getItem("ieltsLogoText") || "A"}
              </div>
            )}
          </a>
          <div className="flex items-center gap-3">
            <a href="/ielts-result" className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700 transition-colors">
              <ArrowRight className="h-3.5 w-3.5" /> My Results
            </a>
            <a href="/" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors">
              <Home className="h-3.5 w-3.5" /> Home
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Result header */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Award className="h-6 w-6 text-indigo-500" />
            <span className="text-sm font-bold text-gray-500 uppercase tracking-widest">
              {result.subject_name} — Result
            </span>
          </div>

          {result.overall_band !== null ? (
            <>
              <p className="text-6xl font-black text-gray-900 mb-1">{result.overall_band}</p>
              <p className="text-sm font-bold text-gray-500 mb-3">Overall Band Score</p>
              <span className={`inline-block rounded-full px-4 py-1.5 text-sm font-bold text-white ${
                result.overall_band >= 8 ? "bg-green-500" : result.overall_band >= 6.5 ? "bg-blue-500" : result.overall_band >= 5.5 ? "bg-amber-500" : "bg-red-500"
              }`}>
                {result.grade_label || ""}
              </span>
            </>
          ) : (
            <div className="py-4">
              <p className="text-2xl font-bold text-gray-700 mb-1">Results Available</p>
              <p className="text-sm text-amber-600">Overall band will be calculated after Writing score is added.</p>
            </div>
          )}

          {result.submitted_at && (
            <p className="text-xs text-gray-400 mt-3">
              Completed on {new Date(result.submitted_at).toLocaleDateString("en-BD", {
                year: "numeric", month: "long", day: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}
            </p>
          )}
        </div>

        {/* Band cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <BandCard
            label="Listening"
            band={result.listening_band}
            score={result.listening_score}
            max={40}
            section="listening"
            detail={listeningDetail}
          />
          <BandCard
            label="Reading"
            band={result.reading_band}
            score={result.reading_score}
            max={40}
            section="reading"
            detail={readingDetail}
          />
          <BandCard
            label="Writing"
            band={result.writing_band}
            score={null}
            max={9}
            section="writing"
            detail={null}
          />
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Listening", val: result.listening_score !== null ? `${result.listening_score}/40` : "—", color: "text-blue-600" },
            { label: "Reading", val: result.reading_score !== null ? `${result.reading_score}/40` : "—", color: "text-green-600" },
            { label: "Overall", val: result.overall_band !== null ? result.overall_band : "Pending", color: "text-indigo-600" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
              <p className="text-xs font-bold uppercase text-gray-400 tracking-wider mb-1">{s.label}</p>
              <p className={`text-2xl font-black ${s.color}`}>{s.val}</p>
            </div>
          ))}
        </div>

        {/* Writing pending notice */}
        {result.writing_band === null && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <span className="text-xl">📝</span>
            </div>
            <div>
              <p className="font-bold text-amber-800 text-sm">Writing Result Pending</p>
              <p className="text-xs text-amber-600 mt-0.5">
                Your Writing will be evaluated by an examiner. Overall Band Score will be available once Writing score is published.
              </p>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3 justify-center">
          <button
            onClick={() => window.location.href = "/ielts"}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 font-bold text-white hover:bg-indigo-700 transition-all shadow-sm hover:scale-105 active:scale-95"
          >
            <ArrowRight className="h-4 w-4" /> Practice Again
          </button>
          <a
            href="/"
            className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-6 py-3 font-bold text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Home className="h-4 w-4" /> Back to Home
          </a>
        </div>
      </main>
    </div>
  );
};

export default IeltsResultPage;